import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "~/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        ghost:
          "text-text-primary border border-border-alpha-10 bg-white/10 hover:bg-brand-25 active:bg-brand-25 transition-all ease-out duration-200",
        link: "text-primary underline-offset-4 hover:underline",
        yellow:
          "bg-gradient-to-r from-[#fff6b5] to-brand-500 text-text-primary border border-border-alpha-10 shadow-[inset_-2px_-2px_6px_0px_rgba(0,0,0,0.06)] hover:opacity-90 transition-opacity",
        primary:
          "text-text-primary opacity-100" +
          " [background:linear-gradient(180deg,#FFECA5_0%,#FFFAC5_7.35%,#FFF984_20.29%,#FFF984_66.89%,#F5EE56_85.56%,#FFE05F_97.12%)]" +
          " [&:hover,&:active]:[background:linear-gradient(180deg,#FFEE8F_0%,#FFFAC5_7.35%,#FFF984_20.29%,#FFF984_66.89%,#F5EE56_85.56%,#FFE05F_97.12%)] [&:hover,&:active]:opacity-70" +
          " transition-all ease-out duration-300 active:duration-0",
        tabTriggerActive:
          "bg-gradient-to-r from-[#fff6b5] to-brand-500 text-text-primary font-semibold rounded-xl transition-all hover:opacity-90",
        tabTriggerInactive:
          "bg-[#f8f7ee] text-text-primary font-medium rounded-xl transition-all hover:bg-[#ecebe3]",
        carouselNav:
          "bg-neutral-50 text-text-primary border border-neutral-300 rounded-xl backdrop-blur-sm shadow-sm hover:bg-[#f5f5f5] transition-all",

        // Legacy sizes for backward compatibility
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
      },
      size: {
        big: "font-bold text-[16px] leading-[24px] rounded-[18px] px-[20px] py-[8px] gap-[4px] h-[48px] [&_svg:not([class*='size-'])]:size-4",
        medium:
          "font-semibold text-[14px] leading-[20px] rounded-[16px] px-[16px] py-[8px] gap-[4px] h-[40px] [&_svg:not([class*='size-'])]:size-4",
        small:
          "font-semibold text-[14px] leading-[20px] rounded-[12px] px-[12px] py-[8px] gap-[4px] h-[32px] [&_svg:not([class*='size-'])]:size-4",
        tiny: "font-medium text-[12px] leading-[18px] rounded-[8px] p-[8px] gap-[2px] h-[28px] [&_svg:not([class*='size-'])]:size-3",

        // Legacy sizes for backward compatibility
        default:
          "h-9 px-4 py-2 gap-2 rounded-md text-sm font-medium [&_svg:not([class*='size-'])]:size-4 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 text-sm font-medium [&_svg:not([class*='size-'])]:size-4 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 gap-2 text-base font-medium [&_svg:not([class*='size-'])]:size-4 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  id,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      id={id}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
